import { DeleteOutlined } from '@ant-design/icons';
import { Avatar, Button, Popconfirm, Skeleton, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Suspense, lazy, useEffect, useState } from 'react';
import { FiEdit } from 'react-icons/fi';
import { MdManageAccounts } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { PROJECT_ROLE_TITLE } from '@/constants/project.enum';

import { useProjectPermissions } from '@/hooks/usePermission';

import { ProjectMember } from '@/interface';

import { deleteProjectMember, getProjectMembersRequest, setSelectedProjectTeamMember } from '@/redux/slices';
import { AppDispatch, RootState } from '@/redux/store';

const AddTeamMemberModal = lazy(() => import('@/pages/dashboard/project/detail/components/addTeamMember'));

export const TeamMembersProjectDetail = () => {
  const hasPermission = useProjectPermissions();
  const canDeleteTeamMember = hasPermission('delete_team_member');
  const canAddMember = hasPermission('add_team_member');
  const canUpdateTeamMember = hasPermission('update_team_member');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { projectId } = useParams<{ projectId: string }>();

  interface Member {
    key: string;
    userId: string;
    name: string;
    email: string;
    status: string;
    role: string;
    roleId: string;
    profilePhotoUrl?: string | null;
  }

  const { projectMembers, loading } = useSelector((state: RootState) => state.project);
  const { userId: currentUserId, user: currentUser } = useSelector((state: RootState) => state.auth);
  const isOwner = currentUser?.role?.trim().toLowerCase() === PROJECT_ROLE_TITLE.OWNER;
  const [users, setUsers] = useState<Member[]>([]);
  const handleEditRole = (member: Member) => {
    if (currentUserId && member.userId === currentUserId) {
      message.warning('You cannot update your own role in this project.');
      return;
    }
    if (member.role?.trim().toLowerCase() === PROJECT_ROLE_TITLE.CLIENT) {
      message.warning('Client role cannot be updated.');
      return;
    }
    dispatch(setSelectedProjectTeamMember(member));
    setIsModalVisible(true);
  };

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectMembersRequest(projectId));
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (projectMembers?.length > 0) {
      const formatted = projectMembers.map((member: ProjectMember) => ({
        key: member.id,
        userId: member.user.id,
        name: member.user.username,
        email: member.user.email,
        status: member.user.status,
        role: member.user.role.title,
        roleId: member.user.role.id,
        profilePhotoUrl: member.user.profilePhotoUrl,
      }));
      setUsers(formatted);
    } else {
      setUsers([]);
    }
  }, [projectMembers]);

  const handleDelete = (key: string, memberUserId?: string) => {
    if (memberUserId && currentUserId && memberUserId === currentUserId) {
      message.warning('You cannot remove yourself from this project.');
      return;
    }
    dispatch(deleteProjectMember(key));
    message.success('Member removed successfully.');
  };

  const canDeleteMember = (member: Member) => {
    if (member.userId === currentUserId) return false;
    if (!canDeleteTeamMember) return false;
    if (member.role?.trim().toLowerCase() === PROJECT_ROLE_TITLE.CLIENT && !isOwner) return false;
    return true;
  };

  const baseColumns: ColumnsType<Member> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record) => (
        <div className="flex items-center gap-2">
          <Avatar src={record.profilePhotoUrl || undefined} size={40} className="bg-primary/20 text-black">
            {text?.charAt(0).toUpperCase()}
          </Avatar>
          <span>{text || '-'}</span>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (text: string) =>
        text === 'skeleton' ? (
          <Skeleton.Input active size="small" style={{ width: 100 }} />
        ) : (
          <div className="capitalize">{text}</div>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      key: 'status',
      render: (status: string) => {
        let displayText = '';
        let colorClass = '';

        switch (status) {
          case 'accepted':
            displayText = 'Added';
            colorClass = 'bg-green-100 text-green-700';
            break;
          case 'pending':
            displayText = 'Not Registered';
            colorClass = 'bg-yellow-100 text-yellow-700';
            break;
          case 'rejected':
            displayText = 'Rejected';
            colorClass = 'bg-red-100 text-red-700';
            break;
          default:
            displayText = 'Unknown';
            colorClass = 'bg-gray-100 text-gray-700';
        }

        return <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorClass}`}>{displayText}</span>;
      },
    },
  ];

  if (canDeleteTeamMember || canUpdateTeamMember) {
    baseColumns.push({
      title: 'Action',
      key: 'action',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {record.userId !== currentUserId &&
            canUpdateTeamMember &&
            record.role?.trim().toLowerCase() !== PROJECT_ROLE_TITLE.CLIENT && (
              <FiEdit className="cursor-pointer  text-lg" title="Edit Title" onClick={() => handleEditRole(record)} />
            )}
          {canDeleteMember(record) && (
            <Popconfirm
              title="Are you sure you want to remove this member?"
              onConfirm={() => handleDelete(record.key, record.userId)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger type="text" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </div>
      ),
    });
  }

  const columns = baseColumns;

  return (
    <>
      {isModalVisible && (
        <Suspense fallback={<div></div>}>
          <AddTeamMemberModal
            onClose={() => {
              setIsModalVisible(false);
            }}
            visible={isModalVisible}
            projectId={projectId!}
          />
        </Suspense>
      )}

      <div className="flex h-full min-h-0 flex-col">
        {canAddMember && (
          <div className="flex flex-shrink-0 justify-end">
            <button
              onClick={() => setIsModalVisible(true)}
              className="pl-2 pr-2.5 py-2 bg-cardClr text-cardClr-text hover:bg-btn hover:text-btn-hover-text rounded-md flex items-center gap-x-2 text-sm"
            >
              <MdManageAccounts className="text-2xl" />
              <div className="font-medium text-sm sm:block hidden">Add Member</div>
            </button>
          </div>
        )}
        <div className="mt-3 min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <Table
            className="custom-table w-full"
            columns={columns}
            dataSource={users}
            pagination={false}
            loading={loading}
            scroll={{ x: 600 }}
          />
        </div>
      </div>
    </>
  );
};
